// Pure-presentational table parts for the data-table-v2 kit (ticket §5.0).
//
// Props-only building blocks originally extracted out of the Analytics
// <DimensionGrid>: they take everything (data, callbacks, scroll/edit flags) via
// props and hold NO store, data-hook, or page coupling, so any table can render
// them in isolation. The consuming table owns all the wiring. This module must
// NOT import a page's store or data hooks.
import { type DragEvent, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { AppIconButton, TableCell, TableHead } from '@clickguard/design-system'
import { ArrowDown, ArrowUp, ChevronSelectorVertical, SearchLg } from './icons'
import { DragGrip } from './drag-grip'

// Expander button — the exact DS expander (AppIconButton, secondary/mini, as
// TableCellExpander uses), with only the chevron glyph swapped for a caret
// (filled triangle, rotates right→down on open).
export const CaretToggle = ({
	expanded,
	onToggle,
	label,
	className = ''
}: {
	expanded: boolean
	onToggle: () => void
	label: string
	className?: string
}) => (
	<AppIconButton
		variant="secondary"
		size="mini"
		aria-label={label}
		aria-expanded={expanded}
		className={className}
		// Row is clickable too; stop the bubble so the caret doesn't double-toggle.
		onClick={(e) => {
			e.stopPropagation()
			onToggle()
		}}
		icon={
			<svg viewBox="0 0 8 8" aria-hidden className={`h-3 w-3 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}>
				<path d="M2.5 1 L6 4 L2.5 7 Z" fill="currentColor" />
			</svg>
		}
	/>
)

// Nested rows animate their height (a table row can only collapse by shrinking
// its cell CONTENT, so the motion wrapper lives inside each <td>). 220ms / house
// easing, matching <CustomizeReveal>. The Dimension column is pinned to a fixed
// width so deeper indentation truncates the label instead of widening the column
// and shoving the metric columns sideways every time a row expands.
export const ANIM_T = { duration: 0.22, ease: [0.16, 1, 0.3, 1] } as const
// The reason-breakdown drawer is a big panel, so the snappy ease-out-expo used for
// slim nested rows reads as a hard slam. It gets its own gentler, slightly longer
// ease-in-out (eases BOTH ends) so opening and closing glide instead.
export const DRAWER_T = { duration: 0.42, ease: [0.4, 0, 0.2, 1] } as const
// Right-edge shadow for the pinned Dimension column once the grid is scrolled.
// A box-shadow on the <td>/<th> itself does NOT paint — the DS Table is
// `border-collapse: collapse`, and Chrome drops cell box-shadows under collapse.
// So each sticky cell hosts an absolutely-positioned gradient strip pinned just
// outside its right edge; it scrolls with the cell and fades in on scroll. The
// strips tile edge-to-edge into one continuous band down the column.
export const StickyEdgeShadow = ({ show, side = 'right' }: { show: boolean; side?: 'left' | 'right' }) => (
	<span
		aria-hidden
		className={`pointer-events-none absolute inset-y-0 z-[1] w-2 transition-opacity duration-200 ${
			side === 'right'
				? 'right-0 translate-x-full bg-gradient-to-r from-[rgba(0,0,0,0.10)] to-[rgba(0,0,0,0)]'
				: 'left-0 -translate-x-full bg-gradient-to-l from-[rgba(0,0,0,0.10)] to-[rgba(0,0,0,0)]'
		} ${show ? 'opacity-100' : 'opacity-0'}`}
	/>
)
// A sticky cell paints its own opaque bg (so scrolled cells don't bleed through),
// which would otherwise MASK the DS row-hover — the pinned column stays white while
// the rest of the row greys, reading as a detached container. So replay the exact DS
// hover (5% black overlay) on the cell via a ::before, gated by the row's `group`
// hover. `-z-[1]` tucks it between the cell bg and its content. Only depth-0 rows carry
// `group`, so nested (shaded, non-hovering) rows never trigger it.
const STICKY_HOVER =
	"before:pointer-events-none before:absolute before:inset-0 before:-z-[1] before:bg-[rgba(0,0,0,0.05)] before:opacity-0 before:transition-opacity before:duration-150 before:content-[''] group-hover:before:opacity-100"

export const GridCell = ({
	animate,
	className,
	width,
	lineClose,
	colSpan,
	transition = ANIM_T,
	sticky = false,
	stickySide = 'left',
	scrolled = false,
	stickyContent = false,
	contentWidth,
	rowDivider = false,
	bg,
	children
}: {
	animate: boolean
	className?: string
	width?: string
	/** Pin to the viewport edge during horizontal scroll (Dimension left, Details right).
	 *  Needs an opaque `bg` so scrolled cells don't show through it. */
	sticky?: boolean
	/** Which edge to pin to (and therefore which side to cast the shadow on). */
	stickySide?: 'left' | 'right'
	/** While there's still content hidden past the pinned edge, cast a soft drop shadow so
	 *  the pinned column reads as floating above the columns sliding under it. */
	scrolled?: boolean
	/** Pin the cell's CONTENT (not the cell) to the viewport so a full-width drawer stays
	 *  in view instead of riding the table's horizontal scroll. Pair with `contentWidth`. */
	stickyContent?: boolean
	/** Viewport-fit width for `stickyContent` (px). Falls back to natural width when unset. */
	contentWidth?: number
	/** The row's bottom divider is a border on the <tr>, which a sticky cell's opaque bg
	 *  paints OVER — so the divider breaks at the pinned columns. Re-draw it on the cell
	 *  itself to keep the line continuous. (Skipped when `lineClose` owns the bottom edge.) */
	rowDivider?: boolean
	bg?: string
	/** Height-collapse easing/duration. Defaults to the slim-row ANIM_T; the
	 *  breakdown drawer passes the gentler DRAWER_T. */
	transition?: typeof ANIM_T | typeof DRAWER_T
	/** Draw the brand "block closes here" line at the <td> bottom (the td is stretched
	 *  to the full row height) so it renders as ONE continuous line across all cells.
	 *  Drawing it on each cell's content wrapper instead put every segment at its own
	 *  content height — a staircase, not a line.
	 *
	 *  Animated cells draw it as an OVERLAID element (sibling of the collapsing
	 *  content, pinned to the td bottom) rather than the td's own border, so on
	 *  row-exit it FADES quickly instead of riding the collapse at full opacity a
	 *  pixel off the divider below (the old `border-b` left that ghost line behind).
	 *  Resting / reduced-motion cells (no exit to glitch) keep the plain border. */
	lineClose?: boolean
	colSpan?: number
	children: ReactNode
}) =>
	animate ? (
		// `sticky` provides the positioning context for the absolute lineClose just
		// as `relative` did, so the two are interchangeable here.
		<TableCell
			colSpan={colSpan}
			className={`${sticky ? `sticky ${stickySide === 'left' ? 'left-0' : 'right-0'} z-[1] ${STICKY_HOVER}${rowDivider && !lineClose ? ' border-b border-b-[rgb(var(--cg-border))]' : ''}` : 'relative'} !p-0 ${width ?? ''}`.trim()}
			style={sticky && bg ? { backgroundColor: bg } : undefined}
		>
			<motion.div
				initial={{ height: 0, opacity: 0 }}
				animate={{ height: 'auto', opacity: 1 }}
				exit={{ height: 0, opacity: 0 }}
				transition={transition}
				// `stickyContent` pins the (overflow-clipped) panel to the viewport: the
				// motion.div sticks relative to the OUTER scroll container — its own
				// overflow:hidden only governs its children, so the pin survives.
				style={stickyContent ? { overflow: 'hidden', position: 'sticky', left: 0, width: contentWidth } : { overflow: 'hidden' }}
			>
				<div className={`px-6 py-4 ${className ?? ''}`.trim()}>{children}</div>
			</motion.div>
			{sticky && <StickyEdgeShadow show={scrolled} side={stickySide === 'left' ? 'right' : 'left'} />}
			{lineClose && (
				<motion.div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-[rgb(var(--cg-brand))]"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.12, ease: 'linear' }}
				/>
			)}
		</TableCell>
	) : (
		<TableCell
			colSpan={colSpan}
			className={`${width ?? ''} ${className ?? ''} ${lineClose ? '!border-b-2 !border-b-[rgb(var(--cg-brand))]' : ''} ${sticky ? `sticky ${stickySide === 'left' ? 'left-0' : 'right-0'} z-[1] ${STICKY_HOVER}${rowDivider && !lineClose ? ' border-b border-b-[rgb(var(--cg-border))]' : ''}` : ''}`.trim() || undefined}
			style={sticky && bg ? { backgroundColor: bg } : undefined}
		>
			{stickyContent ? (
				<div style={{ position: 'sticky', left: 0, width: contentWidth }}>{children}</div>
			) : (
				children
			)}
			{sticky && <StickyEdgeShadow show={scrolled} side={stickySide === 'left' ? 'right' : 'left'} />}
		</TableCell>
	)

// Sortable column header — label + a compact gray up/down affordance. Inactive
// shows the two-way selector; the active column shows a single arrow for its
// direction. Clicking cycles desc → asc → unsorted (handled by the caller).
//
// In Customize mode the metric headers also become drag handles: a grip appears
// and the whole cell is a drop zone (drag handlers supplied by the caller),
// with a brand inset line marking the drop target.
export const SortableHead = ({
	label,
	active,
	dir,
	onClick,
	className,
	sticky = false,
	scrolled = false,
	editMode = false,
	draggable = false,
	onDragStart,
	onDragEnd,
	onDragOver,
	onDrop,
	dropSide = null,
	dragging = false
}: {
	label: string
	active: boolean
	dir: 'asc' | 'desc'
	onClick: () => void
	className?: string
	/** Pin this header to the left during horizontal scroll (the Dimension column). */
	sticky?: boolean
	/** Cast the right-edge shadow once the grid is scrolled (matches the body cells). */
	scrolled?: boolean
	editMode?: boolean
	draggable?: boolean
	onDragStart?: (e: DragEvent) => void
	onDragEnd?: () => void
	onDragOver?: (e: DragEvent) => void
	onDrop?: (e: DragEvent) => void
	/** Side of this header to paint the drop indicator on (null = none). */
	dropSide?: 'left' | 'right' | null
	dragging?: boolean
}) => (
	<TableHead
		onDragOver={editMode ? onDragOver : undefined}
		onDrop={editMode ? onDrop : undefined}
		className={[className, editMode || dropSide ? 'relative' : '', sticky ? 'sticky left-0 z-[2] bg-[rgb(var(--cg-canvas))] border-b border-b-[rgb(var(--cg-border))]' : '']
			.filter(Boolean)
			.join(' ')
			|| undefined}
	>
		{dropSide && (
			<span
				aria-hidden
				className={`pointer-events-none absolute inset-y-1 z-[3] w-0.5 rounded bg-[rgb(var(--cg-brand))] ${dropSide === 'left' ? 'left-0' : 'right-0'}`}
			/>
		)}
		{sticky && <StickyEdgeShadow show={scrolled} />}
		<div className={`flex items-center gap-1.5 transition-opacity ${dragging ? 'opacity-40' : ''}`}>
			{editMode && (
				<span
					draggable={draggable}
					onDragStart={onDragStart}
					onDragEnd={onDragEnd}
					className="-ml-1 flex shrink-0 cursor-grab items-center text-[rgb(var(--cg-text-tertiary))] active:cursor-grabbing"
					aria-label={`Drag to reorder ${label}`}
				>
					<DragGrip />
				</span>
			)}
			<button
				type="button"
				onClick={onClick}
				className="group inline-flex max-w-full items-center gap-1 text-left align-middle"
				aria-label={`Sort by ${label}${active ? (dir === 'asc' ? ', ascending' : ', descending') : ''}`}
			>
				<span className="truncate">{label}</span>
				{/* Same gray whether sorted or not — only the glyph changes (selector →
				    single arrow), never the colour. */}
				<span className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-500">
					{active ? (
						dir === 'asc' ? (
							<ArrowUp className="h-3.5 w-3.5" />
						) : (
							<ArrowDown className="h-3.5 w-3.5" />
						)
					) : (
						<ChevronSelectorVertical className="h-3.5 w-3.5" />
					)}
				</span>
			</button>
		</div>
	</TableHead>
)

// Empty state — shown when search / filters return no rows. A soft layered brand
// badge (concentric tints + a floating white tile) over a short prompt and a
// one-tap reset. The outer ring breathes; the block fades up on mount. Reduced
// motion gets a static badge.
export const TableEmptyState = ({ message, onClear, clearLabel, reduced }: { message: ReactNode; onClear?: () => void; clearLabel?: string; reduced: boolean }) => (
	<motion.div
		initial={reduced ? false : { opacity: 0, y: 8 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
		className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center"
	>
		<div className="relative flex h-16 w-16 items-center justify-center">
			<motion.span
				aria-hidden
				className="absolute inset-0 rounded-2xl bg-[rgba(85,75,253,0.06)]"
				animate={reduced ? undefined : { opacity: [0.5, 1, 0.5], scale: [0.96, 1, 0.96] }}
				transition={reduced ? undefined : { duration: 3, ease: 'easeInOut', repeat: Infinity }}
			/>
			<span aria-hidden className="absolute inset-[10px] rounded-xl bg-[rgba(85,75,253,0.1)]" />
			<span className="relative flex h-9 w-9 items-center justify-center rounded-[10px] bg-white shadow-sm ring-1 ring-[#ececf5]">
				<SearchLg className="h-4 w-4 text-[rgb(var(--cg-brand))]" />
			</span>
		</div>
		<div className="space-y-1">
			<p className="text-[15px] font-semibold text-[rgb(var(--cg-text-primary))]">No matching rows</p>
			<p className="mx-auto max-w-[320px] text-[13px] leading-5 text-gray-500">{message}</p>
		</div>
		{onClear && (
			<button
				type="button"
				onClick={onClear}
				className="text-[13px] font-semibold text-[rgb(var(--cg-brand))] transition-colors hover:text-[#3a33ab] hover:underline"
			>
				{clearLabel}
			</button>
		)}
	</motion.div>
)
